import { useMemo } from 'react';
import { Formik, Form } from 'formik';
import {
  object,
  array,
  number,
  boolean,
  string,
  mixed,
  StringSchema,
  SchemaOf,
} from 'yup';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../../types';
import { useNameValidation } from '../../validation/useNameValidation';
import { PolicyConfigurationFormSection } from '../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../PolicyFormFooter';

import { ChangeConfirmationFormFields } from './ChangeConfirmationFormFields';
import {
  AT_LEAST_ONE_ERROR_KEY,
  ChangeConfirmationFormValues,
  ConfirmSeverity,
} from './types';
import { useChangeConfirmationInitialValues } from './useChangeConfirmationInitialValues';

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
const MAX_BANNER_MESSAGE_LENGTH = 200;
const MAX_CONFIRM_TITLE_LENGTH = 200;
const MAX_CONFIRM_MESSAGE_LENGTH = 2000;

interface Props {
  onSubmit: (values: ChangeConfirmationFormValues) => Promise<void>;
  mode: 'create' | 'edit';
  policy?: TypedPolicy;
  templateData?: Record<string, unknown>;
  onBack?: () => void;
  canGoBack?: boolean;
}

function createValidationSchema(
  nameValidation: StringSchema<string>
): SchemaOf<ChangeConfirmationFormValues> {
  return object({
    name: nameValidation,
    environmentGroupIds: array(number().required()),
    highlightEnabled: boolean().required(),
    highlightColor: string().when('highlightEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Please choose a color to highlight the environment.')
          .matches(
            HEX_COLOR_REGEX,
            'Highlight color must be a valid hex (e.g. #ffbbbb or #fff)'
          ),
      otherwise: (schema) => schema,
    }),
    bannerMessage: string().max(
      MAX_BANNER_MESSAGE_LENGTH,
      `Banner message must not exceed ${MAX_BANNER_MESSAGE_LENGTH} characters`
    ),
    confirmEnabled: boolean().required(),
    confirmTitle: string().when('confirmEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Please enter a title for the confirmation dialog.')
          .max(
            MAX_CONFIRM_TITLE_LENGTH,
            `Title must not exceed ${MAX_CONFIRM_TITLE_LENGTH} characters`
          ),
      otherwise: (schema) => schema,
    }),
    confirmMessage: string().when('confirmEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required(
            'Please enter a message to show in the confirmation dialog.'
          )
          .max(
            MAX_CONFIRM_MESSAGE_LENGTH,
            `Message must not exceed ${MAX_CONFIRM_MESSAGE_LENGTH} characters`
          ),
      otherwise: (schema) => schema,
    }),
    confirmSeverity: mixed<ConfirmSeverity>()
      .oneOf(['default', 'warning', 'error'])
      .required(),
  }).test(
    AT_LEAST_ONE_ERROR_KEY,
    'Please enable at least one option: highlight environment or change confirmation.',
    // eslint-disable-next-line func-names
    function (value) {
      const valid =
        value &&
        (value.highlightEnabled === true || value.confirmEnabled === true);
      if (valid) return true;
      return this.createError({
        path: AT_LEAST_ONE_ERROR_KEY,
        message:
          'Please enable at least one option: highlight environment or change confirmation.',
      });
    }
  );
}

export function ChangeConfirmationPolicyForm({
  onSubmit,
  mode,
  policy,
  templateData,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useChangeConfirmationInitialValues(
    policy,
    templateData
  );
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups ?? [];
  const nameValidation = useNameValidation(policy?.Id);
  const validationSchema = useMemo(
    () => createValidationSchema(nameValidation),
    [nameValidation]
  );

  return (
    <Formik<ChangeConfirmationFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <Form className="form-horizontal">
        <Widget>
          <WidgetBody>
            {mode === 'create' && (
              <PolicyConfigurationFormSection policyType="change-confirmation" />
            )}
            <ChangeConfirmationFormFields />
            {mode === 'edit' && (
              <PolicyFormFooter
                mode={mode}
                policyType="change-confirmation"
                policyId={policy?.Id ?? null}
                originalEnvironmentGroupIds={originalEnvironmentGroupIds}
              />
            )}
          </WidgetBody>
        </Widget>

        {mode === 'create' && (
          <PolicyFormFooter
            mode={mode}
            policyType="change-confirmation"
            policyId={policy?.Id ?? null}
            onBack={onBack}
            canGoBack={canGoBack}
          />
        )}
      </Form>
    </Formik>
  );
}
